import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Download, X, Share2 } from "lucide-react";

const DISMISS_KEY = "hk-pwa-install-dismissed-at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const TARGET_ROLES = ["production", "installation"];

/** Floating "Add to Home Screen" prompt.
 * - Only shown to production / installation users on their phones
 * - Uses the beforeinstallprompt event where the browser supports it
 *   (Chrome / Edge / Android). iOS Safari doesn't fire that event, so we
 *   fall back to a short instruction card explaining the Share → Add flow.
 * - Dismissed with 7-day snooze. */
export default function InstallPrompt() {
  const { user } = useAuth();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (!user || !TARGET_ROLES.includes(user.role)) return;
    // Already installed / running as PWA — bail.
    const standalone = window.matchMedia?.("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (standalone) return;
    // Recently dismissed — respect snooze.
    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed && Date.now() - dismissed < DISMISS_TTL_MS) return;
    const ua = navigator.userAgent || "";
    const iosLike = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(iosLike);
    if (iosLike) {
      // Show the manual instruction card on iOS.
      setVisible(true);
      return;
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, [user]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        setVisible(false);
      } else {
        dismiss();
      }
    } catch (_) { /* silent */ }
  };

  if (!visible) return null;
  return (
    <div
      role="dialog"
      aria-labelledby="pwa-install-title"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-[520px]"
      data-testid="pwa-install-prompt"
    >
      <div className="rounded-sm border-2 border-orange-500 bg-white shadow-2xl px-4 py-3 flex items-start gap-3">
        <div className="w-10 h-10 rounded-sm bg-orange-500 text-white flex items-center justify-center shrink-0">
          <Download className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div id="pwa-install-title" className="text-sm font-black text-slate-900">
            Add HK Shop Floor to your phone
          </div>
          {isIOS ? (
            <p className="text-[11px] text-slate-600 mt-1 leading-snug">
              Tap the <Share2 className="w-3 h-3 inline mx-0.5 -mt-0.5" /> Share icon in Safari, then
              choose <b>Add to Home Screen</b>. Opens like a native app.
            </p>
          ) : (
            <p className="text-[11px] text-slate-600 mt-1 leading-snug">
              Install this app for one-tap launch, full-screen mode and offline-ready shell.
            </p>
          )}
          <div className="mt-2 flex items-center gap-2">
            {!isIOS && (
              <button onClick={install} data-testid="pwa-install-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-black uppercase px-3 py-1.5 rounded-sm">
                Install now
              </button>
            )}
            <button onClick={dismiss} data-testid="pwa-install-dismiss"
              className="text-slate-500 hover:text-slate-800 text-xs font-black uppercase px-2 py-1.5">
              Not now
            </button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="Close" className="text-slate-400 hover:text-slate-700 shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
